// Timestamp + GPS watermark for camera-captured claim photos only (not
// gallery uploads, not videos) — mirrors the existing guest-inspection
// flow's approach (apps/web/src/app/(guest)/inspect/[tenant_slug]/media/
// _watermark.ts) rather than introducing a new capture mechanism: the
// image is drawn onto an offscreen canvas, stamped with text, and
// re-exported as the file that actually gets uploaded.
//
// normalizeImageFile (below) reuses the same canvas round-trip WITHOUT the
// stamp, for gallery / "Upload" / "Replace" picks: phones hand those files
// to the browser with a wrong or missing content-type (image/jpg,
// application/octet-stream, HEIC from an iPhone) which the claim-media API
// used to hard-reject. Re-encoding to a real image/jpeg blob fixes the
// content-type at the source and also downscales huge originals so the
// upload does not time out on a mobile connection.

export interface GPS {
  lat: number;
  lng: number;
}

// Anything wider/taller than this is downscaled before upload — keeps a
// gallery pick well under the API's 20 MB photo cap and off the 408-timeout
// path on slow mobile uplinks. 2560px preserves plenty of damage detail.
const MAX_DIMENSION = 2560;
const JPEG_QUALITY = 0.9;

export function drawWatermark(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  gps: GPS | null,
): void {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-IN', { hour12: true });
  const gpsLine = gps
    ? `${gps.lat.toFixed(6)}° N,  ${gps.lng.toFixed(6)}° E`
    : 'GPS unavailable';

  const lines = [`${dateStr}  ${timeStr}`, gpsLine];
  const fontSize = Math.max(Math.round(width * 0.03), 14);
  const lineHeight = fontSize * 1.6;
  const padding = fontSize * 0.6;
  const boxHeight = lines.length * lineHeight + padding * 2;

  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.58)';
  ctx.fillRect(0, height - boxHeight, width, boxHeight);

  ctx.font = `bold ${fontSize}px 'Courier New', monospace`;
  ctx.fillStyle = '#FFFFFF';
  ctx.textBaseline = 'top';
  lines.forEach((line, lineIndex) => {
    ctx.fillText(line, padding, height - boxHeight + padding + lineIndex * lineHeight);
  });
  ctx.restore();
}

interface RenderOptions {
  stamp: GPS | null | false; // false = no watermark box at all
}

// Shared canvas round-trip: decode -> (optional downscale) -> (optional
// stamp) -> re-encode as image/jpeg. Rejects if the browser cannot decode
// the source (e.g. an unsupported HEIC on an old Android) so the caller can
// fall open to the original file.
function renderToJpegFile(file: File, { stamp }: RenderOptions): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { naturalWidth: width, naturalHeight: height } = img;
      if (width === 0 || height === 0) {
        reject(new Error('Image has no dimensions'));
        return;
      }
      const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      if (stamp !== false) {
        drawWatermark(ctx, width, height, stamp);
      }
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Image processing failed'));
            return;
          }
          const baseName = file.name.replace(/\.[^./\\]+$/, '') || 'photo';
          resolve(new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        JPEG_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not load image'));
    };
    img.src = objectUrl;
  });
}

/** Camera capture: re-encode + stamp date/GPS. */
export async function applyWatermarkToFile(file: File, gps: GPS | null): Promise<File> {
  return renderToJpegFile(file, { stamp: gps });
}

/**
 * Gallery / "Upload" / "Replace" pick: re-encode to a clean image/jpeg
 * (no watermark) so a phone's wrong/missing content-type never reaches the
 * API, and downscale oversized originals. Falls open to the original file
 * if the browser can't decode it or it isn't an image at all.
 */
export async function normalizeImageFile(file: File): Promise<File> {
  if (!file.type.startsWith('image/') && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
    // Not something we should be re-encoding (e.g. a stray video in a photo
    // slot) — hand it straight through and let the server reject it.
    return file;
  }
  try {
    return await renderToJpegFile(file, { stamp: false });
  } catch {
    return file;
  }
}
